import { Injectable } from "@angular/core";
import { Subject, Observable, BehaviorSubject } from "rxjs";
import { Socket } from "../../infrastructure/socket.service";
import { RecordingRepository } from "../../domain/recording-repository";
import {
  RecordingJob,
  RecordingJobStatus,
  RecordingAudioQuality,
  RecordingVideoQuality,
} from "../../domain/recording-job";
import { RecordingSource } from "../../domain/recording-source";
import { RecordingStatus, Recording } from "../../domain/recording";
import moment from "moment";
import { v4 as uuid } from "uuid";

@Injectable({ providedIn: "root" })
export class RecordingService {
  public showLastCountSelectedValue: number = 25;
  public showLastCountOptions: { displayName: string; value: number }[] = [
    /*{ displayName: '1', value: 1},{ displayName: '5', value: 5},*/ {
      displayName: "25",
      value: 25,
    },
    { displayName: "100", value: 100 },
    { displayName: "All", value: 9223372036854775807 },
  ];

  public startDateFrom?: moment.Moment;
  public startDateTo?: moment.Moment;

  private initilized = false;
  //private _recordingJobs: Array<RecordingJob>;
  private _recordingJobCreatedSubject: BehaviorSubject<string> =
    new BehaviorSubject<string>("");
  private _recordingJobUpdatedSubject: BehaviorSubject<string> =
    new BehaviorSubject<string>("");
  private _recordingJobDeletedSubject: BehaviorSubject<string> =
    new BehaviorSubject<string>("");
  //private _recordingHistoricalJobsSubject: BehaviorSubject<void> = new BehaviorSubject<void>();
  private _recordingJobsSubject: BehaviorSubject<void> =
    new BehaviorSubject<void>(null);
  private _createNewRecordingJob: Subject<void> = new Subject<void>();
  private _failureAnnouncerSubject: Subject<string> = new Subject<string>();
  constructor(
    private socketService: Socket,
    private recordingReository: RecordingRepository,
  ) {}

  public init() {
    if (!this.initilized) {
      this.socketService.recordingsHubConnection
        .invoke("StartListeningToRecordingJobChangedEvent")
        .then(() => {
          this.socketService.recordingsHubConnection.on(
            "recordingJobStatusUpdated",
            this.recordingJobUpdated.bind(this),
          );
          this.socketService.recordingsHubConnection.on(
            "recordStatusUpdated",
            this.recordingJobUpdated.bind(this),
          );
          console.log(
            "Successfully started listening to recording job changes",
          );
          this.initilized = true;
        })
        .catch((error) => console.log(error));
    }
  }

  public getRecordingJobCreatedObserver(): Observable<string> {
    return this._recordingJobCreatedSubject.asObservable();
  }
  public getRecordingJobUpdatedObserver(): Observable<string> {
    return this._recordingJobUpdatedSubject.asObservable();
  }
  public getRecordingJobDeletedObserver(): Observable<string> {
    return this._recordingJobDeletedSubject.asObservable();
  }
  /*public getRecordingHistoricalJobCacheUpdateObserver(): Observable<Array<RecordingJob>> {
        return this._recordingHistoricalJobsSubject.asObservable();
    }*/
  public getRecordingJobCacheUpdateObserver(): Observable<void> {
    return this._recordingJobsSubject.asObservable();
  }
  public getCreateNewRecordingJobObserver(): Observable<void> {
    return this._createNewRecordingJob.asObservable();
  }

  public getFailureAnnouncerObserver(): Observable<string> {
    return this._failureAnnouncerSubject.asObservable();
  }

  public callCreateNewRecordingJob(): void {
    this._createNewRecordingJob.next();
  }

  public initEmptyRecordingJob(): RecordingJob {
    const now = moment.utc();
    return new RecordingJob(
      uuid(),
      ``,
      now,
      now.clone().add(3, "h"),
      RecordingJobStatus.Scheduled,
      RecordingVideoQuality.High,
      RecordingAudioQuality.High,
      [],
    );
  }

  public async GetRecordingSourceMediaId(
    recordingSourceId: string,
    recordingJobId: string,
  ): Promise<number> {
    try {
      const mediaId = await this.recordingReository.getRecordingMediaId(
        null,
        recordingJobId,
        recordingSourceId,
      );
      return mediaId;
    } catch {}
    return undefined;
  }

  public async isRecordingSourceRecording(
    referenceId: string,
  ): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      try {
        //if (!this._recordingJobs) { return false; }
        const scheduledJobs = await this.recordingReository.getRecordingJobs(
          null,
          null,
          null,
          null,
          null,
          [RecordingJobStatus.Running],
        );
        const sources = scheduledJobs.flatMap((x) => x.recordingSources);
        resolve(sources.map((x) => x.referenceId).indexOf(referenceId) > -1);
      } catch {}
      resolve(false);
    });
  }
  /*public async refreshJobsCache() {
      this._recordingJobs = null;
      this._recordingJobs = await this.getAll();
      console.log('recording-service-> running jobs: ', this._recordingJobs.filter(x => x.jobStatus === 1));
      this._recordingJobsSubject.next();
    }*/
  public async createNewRecordingJobForSource(
    source: RecordingSource,
  ): Promise<boolean> {
    const job = this.initEmptyRecordingJob();
    job.name = `${source.name}_${moment.utc().format("YYYYMMDDHHmmss")}`;
    job.jobStatus = RecordingJobStatus.Scheduled;
    source.id = uuid();
    job.recordingSources.push(source);
    try {
      return await this.createRecordingJob(job);
    } catch {
      this.announceFailure(`failed to create new recording for ${source.name}`);
      return false;
    }
  }

  public async get(
    selectTop?: number,
    startDateFrom?: moment.Moment,
    startDateTo?: moment.Moment,
    recordId?: number,
    jobId?: string,
    statuses?: RecordingJobStatus[],
    orderByCreatedTimeDesc?: boolean,
  ): Promise<Array<RecordingJob>> {
    const result = await this.recordingReository.getRecordingJobs(
      selectTop,
      startDateFrom,
      startDateTo,
      recordId,
      jobId,
      statuses,
      orderByCreatedTimeDesc,
    );
    //this._recordingJobsSubject.next(null);
    return result;
  }

  /*public async getHistoricalJobs(): Promise<Array<RecordingJob>> {
        if (!this._recordingJobs) {
            await this.getAll();
        }
        return new Promise<RecordingJob[]>((resolve) => {
            const now = moment.utc();
            const historicalJobs = this._recordingJobs.filter(x =>
                now.isAfter(x.endTime)
                || x.jobStatus === RecordingJobStatus.Finished
                || x.jobStatus === RecordingJobStatus.Stopped);
            resolve(historicalJobs);
        });
    }*/
  public async createRecordingJob(
    recordingJob: RecordingJob,
  ): Promise<boolean> {
    recordingJob.fixStatusBeforeUpdatingServer();
    //this.pushRecordingJobToCache(recordingJob);
    return new Promise<boolean>((resolve) => {
      this.recordingReository
        .createRecordingJob(recordingJob)
        .then((id) => {
          if (recordingJob.id === id) {
            //this.recordingJobUpdated(id);
            resolve(true);
            this._recordingJobCreatedSubject.next(id);
          } else {
            //this.popRecordingJobFromCache(recordingJob);
            this.announceFailure(
              `Failed to create new recording: ${recordingJob.name}`,
            );
            resolve(false);
          }
        })
        .catch((err) => {
          //this.popRecordingJobFromCache(recordingJob);
          this.announceFailure(
            `Failed to create new recording: ${recordingJob.name}<br>Reason: ${err.message}`,
          );
          resolve(err);
        });
    });
  }

  public async updateRecordingJob(
    recordingJob: RecordingJob,
  ): Promise<boolean> {
    recordingJob.fixStatusBeforeUpdatingServer();
    //this.updateRecordingJobInCache(recordingJob);
    return new Promise<boolean>((resolve) => {
      this.recordingReository
        .updateRecordingJob(recordingJob)
        .then((result) => {
          this._recordingJobUpdatedSubject.next(recordingJob.id);
          //this.recordingJobUpdated(result);
        })
        .catch((err) => {
          //this.recordingJobUpdated(recordingJob.id);
          this.announceFailure(`Failed to update: ${recordingJob.name}<br>Reason: ${err.message}`);
          resolve(err);
        });
    });
  }

  public async deleteRecordingJob(
    recordingJob: RecordingJob,
  ): Promise<boolean> {
    //this.popRecordingJobFromCache(recordingJob);
    return new Promise<boolean>((resolve) => {
      this.recordingReository
        .deleteRecordingJob(recordingJob.id)
        .then((result) => {
          if (result) {
            this._recordingJobUpdatedSubject.next(recordingJob.id);
            resolve(true);
          } else {
            //this.pushRecordingJobToCache(recordingJob);
            this.announceFailure(`Failed to delete: ${recordingJob.name}`);
            resolve(false);
          }
        })
        .catch((err) => {
          //this.pushRecordingJobToCache(recordingJob);
          this.announceFailure(`Failed to delete: ${recordingJob.name}<br>Reason: ${err.message}`);
          resolve(false);
        });
    });
  }
  /*private async updateHistoricalRecordingJobs(): Promise<void> {
      this._recordingHistoricalJobsSubject.next(jobs);
    }*/
  private async recordingJobUpdated(jobId: string): Promise<void> {
    /*const recordingJob = await this.getRecordingJobById(jobId);
        if (recordingJob) {
            const jobInCache = this._recordingJobs.find(x => x.id === recordingJob.id);
            if (jobInCache) {
                this.updateRecordingJobInCache(recordingJob);
            } else {
                this.pushRecordingJobToCache(recordingJob);
            }
            if (recordingJob.endTime.isBefore(moment.utc()) || recordingJob.jobStatus === RecordingJobStatus.Finished) {
                this.updateHistoricalRecordingJobs();
            }
        } else {
            this.popRecordingJobFromCache(recordingJob);
        }*/
    this._recordingJobsSubject.next(null);
  }

  private async announceFailure(msg: string): Promise<void> {
    this._failureAnnouncerSubject.next(msg);
  }
  /*private pushRecordingJobToCache(recordingJob: RecordingJob): void {
        this._recordingJobs.push(recordingJob);
        if (recordingJob.endTime.isAfter(moment.utc())) {
            this._recordingJobCreatedSubject.next(recordingJob.id);
        } else {
            this.updateHistoricalRecordingJobs();
        }
    }*/

  /*private updateRecordingJobInCache(recordingJob: RecordingJob): void {
        const index = this._recordingJobs.findIndex(x => x.id === recordingJob.id);
        if (index > -1) {
            this._recordingJobs[index] = recordingJob;
            const now = moment.utc();
            if (recordingJob.endTime.valueOf() <= now.valueOf()) {
                this.updateHistoricalRecordingJobs();
            }
            this._recordingJobUpdatedSubject.next(recordingJob.id);
        }
    }*/

  /*private popRecordingJobFromCache(recordingJob: RecordingJob): void {
        const index = this._recordingJobs.findIndex(x => x.id === recordingJob.id);
        if (index > -1) {
            this._recordingJobs.splice(index, 1);
            this._recordingJobDeletedSubject.next(recordingJob.id);
            this.updateHistoricalRecordingJobs();
        }
    }*/
}
